import { useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HStack, Button, useDisclosure, useToast } from '@chakra-ui/react';
import { DownloadIcon, EmailIcon, ArrowForwardIcon } from '@chakra-ui/icons';

import { apiCreateAutoQuotePolicy, apiCreatePropertyQuotePolicy } from 'api';
import { LOB_AUTO, type QuoteLOB } from '../../constants';
import { useQuoteLock, buildPolicyUrl } from '../../hooks';
import ProceedToApplicationModal from './ProceedToApplicationModal';
import PrintOptionsModal, { type PrintOptionKey } from './PrintOptionsModal';

interface QuoteActionButtonsProps {
  quoteId: number;
  lob: QuoteLOB;
}

const QuoteActionButtons = ({ quoteId, lob }: QuoteActionButtonsProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isLocked, policyUrl } = useQuoteLock();
  const {
    isOpen: isProceedOpen,
    onOpen: onProceedOpen,
    onClose: onProceedClose,
  } = useDisclosure();
  const {
    isOpen: isPrintOpen,
    onOpen: onPrintOpen,
    onClose: onPrintClose,
  } = useDisclosure();

  const proceedMutation = useMutation({
    mutationFn: () =>
      lob === LOB_AUTO
        ? apiCreateAutoQuotePolicy(quoteId)
        : apiCreatePropertyQuotePolicy(quoteId),
    onSuccess: ({ policy_uuid, submission_id }) => {
      onProceedClose();
      queryClient.invalidateQueries({
        queryKey: ['marketplace-quote', quoteId],
      });
      navigate(buildPolicyUrl(submission_id, policy_uuid, 'validations') ?? '');
    },
    onError: (error: any) => {
      onProceedClose();
      toast({
        title: 'Failed to proceed to application',
        description: error?.response?.data?.error || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const handlePrint = useCallback(
    (selectedOptions: PrintOptionKey[]) => {
      // TODO: Implement print logic based on selected options
      console.log('Print requested with options:', selectedOptions);
      onPrintClose();
    },
    [onPrintClose],
  );

  return (
    <>
      <HStack spacing={3} justify="flex-end" mt={6}>
        <Button
          leftIcon={<DownloadIcon />}
          variant="outline"
          size="md"
          onClick={onPrintOpen}
        >
          Print
        </Button>
        <Button leftIcon={<EmailIcon />} variant="outline" size="md" isDisabled>
          Send To Client
        </Button>
        {isLocked ? (
          policyUrl && (
            <Button
              as={RouterLink}
              to={policyUrl}
              rightIcon={<ArrowForwardIcon />}
              colorScheme="brand"
              size="md"
            >
              View Application
            </Button>
          )
        ) : (
          <Button
            leftIcon={<ArrowForwardIcon />}
            colorScheme="blue"
            size="md"
            onClick={onProceedOpen}
          >
            Proceed to Application
          </Button>
        )}
      </HStack>

      <PrintOptionsModal
        isOpen={isPrintOpen}
        onClose={onPrintClose}
        onPrint={handlePrint}
      />

      <ProceedToApplicationModal
        isOpen={isProceedOpen}
        onClose={onProceedClose}
        onConfirm={() => proceedMutation.mutate()}
        isLoading={proceedMutation.isPending}
      />
    </>
  );
};

export default QuoteActionButtons;
